import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import componentStyles from './pc-menu-dropdown.css.lit';
import globalStyles from './pc-menu-dropdown.lightDom.css.lit';

import '../pc-icon/pc-icon';

/**
 * Menu dropdown component
 * @element pc-menu-dropdown
 * @slot - This element has a default slot
 */
@customElement('pc-menu-dropdown')
export class PCMenuDropdown extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;
  static styles: CSSResultArray = [componentStyles];

  items: Array<Element> = [];

  @property({ type: Boolean, attribute: 'has-ancillary-slot', reflect: true })
  hasAncillarySlot = false;

  @property({ type: Number })
  level = 0;

  @property({ type: Boolean, attribute: 'is-expanded', reflect: true })
  isExpanded = false;

  @property({ attribute: 'trigger-label' })
  triggerLabel: string | null;

  @property({ attribute: 'back-label' })
  backLabel: string | null;

  @query('.dropdown')
  _dropdown: HTMLDivElement;

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    const link = this.querySelector('[slot="primary"]')?.children ?? [];
    const ancillarySlot = this.querySelector('[slot="ancillary"]');

    // Initializing menu items.
    this.items = [...link];

    if (ancillarySlot) {
      this.items.concat(ancillarySlot);
      this.hasAncillarySlot = true;
    }
  }

  _hideElements = (element: Element) => {
    this.items
      .filter(item => item !== element)
      .forEach((item: Element) => item.classList?.add('is-hidden'));
  };

  _displayElements = () => {
    this.items.forEach((item: Element) => item.classList?.remove('is-hidden'));
  };

  _collapseContent = () => {
    this.isExpanded = false;
    const event = new CustomEvent('collapse-item', {
      detail: { item: this },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(event);
  };

  _expandContent = () => {
    this.isExpanded = true;
    const event = new CustomEvent('expand-item', {
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(event);
  };

  _toggleContent = () => {
    if (this.isExpanded) {
      this._collapseContent();
    } else {
      this._expandContent();
    }
  };

  render(): TemplateResult {
    return html`
      <div class="dropdown level-${this.level}">
        <button
          class="trigger-button"
          @click=${this._toggleContent}
          aria-label="Toggle dropdown menu"
          aria-expanded=${this.isExpanded ? 'true' : 'false'}
          aria-controls=${this.id ? `dropdown-${this.id}` : 'dropdown'}
          aria-haspopup="true"
        >
          ${this.triggerLabel}
          <pc-icon name="chevron-right"></pc-icon>
          <pc-icon name="chevron-down" size="sm"></pc-icon>
        </button>

        <div
          class="content ${this.hasAncillarySlot ? 'has-ancillary' : ''}"
          id=${this.id ? `dropdown-${this.id}` : 'dropdown'}
        >
          <button
            class="close-button"
            @click=${this._collapseContent}
            aria-label="Close dropdown menu"
          >
            <pc-icon name="close"></pc-icon>
          </button>
          <div
            class="primary-section"
            @collapse-item=${this._collapseItemHandler}
            @expand-item=${this._expandItemHandler}
          >
            <div class="heading">
              <button
                class="back-button"
                @click=${this._collapseContent}
                aria-label="Go back previous menu"
              >
                <pc-icon name="chevron-left"></pc-icon> ${this.backLabel}
              </button>
              <slot></slot>
            </div>

            <div class="links">
              <slot name="primary"></slot>
            </div>
          </div>
          <div class="ancillary-section">
            <slot name="ancillary"></slot>
          </div>
        </div>
      </div>
    `;
  }

  private _collapseItemHandler(event: CustomEvent) {
    event.stopPropagation();
    this._displayElements();
  }

  private _expandItemHandler(event: CustomEvent) {
    event.stopPropagation();
    this._hideElements(event.target as Element);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-menu-dropdown': PCMenuDropdown;
  }
}
